import React, { useState, useEffect } from 'react';
import { Clock, Cpu, Wifi, Server, Globe, Info, Database, Shield, Activity, Download, RotateCw, Copy, Check } from 'lucide-react';

const NetworkInfoDashboard = () => {
  const [networkInfo, setNetworkInfo] = useState({
    ip: 'Loading...',
    ipv4: 'Loading...',
    ipv6: 'Loading...',
    xffIp: 'Loading...',
    userAgent: 'Loading...',
    language: 'Loading...',
    platform: 'Loading...',
    screenRes: 'Loading...',
    cores: 'Loading...',
    connection: 'Loading...',
    rtt: 'Loading...',
    downlink: 'Loading...',
    webGLRenderer: 'Loading...',
    batteryLevel: 'Loading...',
    memory: 'Loading...',
    timeZone: 'Loading...',
    architecture: 'Loading...',
    deviceType: 'Loading...',
    osName: 'Loading...',
    osVersion: 'Loading...',
    cpuClass: 'Loading...'
  });
  
  const [speedTest, setSpeedTest] = useState({
    downloadSpeed: null,
    uploadSpeed: null,
    isRunning: false,
    progress: 0
  });
  
  const [systemTime, setSystemTime] = useState(new Date().toISOString());
  const [expandedSection, setExpandedSection] = useState(null);

  useEffect(() => {
    // Function to fetch IP addresses from public APIs
    const fetchIPAddresses = async () => {
      let ipv4 = 'Not available';
      let ipv6 = 'Not available';
      let xffIp = 'Not available';
      let proxyDetected = false;
      
      // Try to fetch IPv4 address
      try {
        const ipv4Response = await fetch('https://api.ipify.org?format=json');
        const ipv4Data = await ipv4Response.json();
        ipv4 = ipv4Data.ip;
      } catch (error) {
        console.error('Error fetching IPv4:', error);
      }
      
      // Try to fetch IPv6 address
      try {
        const ipv6Response = await fetch('https://api64.ipify.org?format=json');
        const ipv6Data = await ipv6Response.json();
        if (ipv6Data.ip && ipv6Data.ip.includes(':')) {
          ipv6 = ipv6Data.ip;
        } else if (ipv6Data.ip !== ipv4) {
          ipv6 = ipv6Data.ip + ' (may be IPv4 fallback)';
        }
      } catch (error) {
        console.error('Error fetching IPv6:', error);
      }
      
      // Try multiple services for XFF/proxy detection
      const headerServices = [
        {
          name: 'ipapi.co',
          url: 'https://ipapi.co/json/',
          extract: (data) => {
            // This service sometimes includes proxy information
            if (data.proxy === true || data.threat_types?.includes('proxy')) {
              proxyDetected = true;
            }
            return data.ip;
          }
        },
        {
          name: 'ip-api.com',
          url: 'http://ip-api.com/json/?fields=status,message,query,proxy',
          extract: (data) => {
            if (data.status === 'success' && data.proxy) {
              proxyDetected = true;
            }
            return data.query;
          }
        },
        {
          name: 'ifconfig.me',
          url: 'https://ifconfig.me/all.json',
          extract: (data) => {
            // Check if X-Forwarded-For is present in the response
            if (data['X-Forwarded-For'] || data.forwarded) {
              const xffString = data['X-Forwarded-For'] || data.forwarded;
              xffIp = parseXFF(xffString);
              proxyDetected = true;
            }
            return data.ip_addr;
          }
        }
      ];
      
      // Try each service
      for (const service of headerServices) {
        try {
          console.log(`Trying ${service.name}...`);
          const response = await fetch(service.url, {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
            },
            // Add timeout
            signal: AbortSignal.timeout(5000) // 5 second timeout
          });
          
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
          
          const contentType = response.headers.get('content-type');
          if (!contentType?.includes('application/json')) {
            throw new Error('Response is not JSON');
          }
          
          const data = await response.json();
          console.log(`${service.name} response:`, data);
          
          const ip = service.extract(data);
          if (ip && ip !== ipv4) {
            xffIp = ip;
            proxyDetected = true;
            break; // Found proxy info, stop trying other services
          }
        } catch (error) {
          console.warn(`${service.name} failed:`, error.message);
          continue; // Try next service
        }
      }
      
      return {
        primary: ipv6 !== 'Not available' ? ipv6 : ipv4,
        ipv4,
        ipv6,
        xff: parseXFF(xffIp),
        proxyDetected
      };
    };

    // Add this helper function at the top of the fetchIPAddresses function
    const parseXFF = (xffString) => {
      if (!xffString || typeof xffString !== 'string') {
        return 'Not available';
      }
      
      // Split by comma and take the first IP, trimming whitespace
      const xffArray = xffString.split(',').map(ip => ip.trim());
      return xffArray[0];
    };

    // Function to get WebGL renderer info
    const getWebGLInfo = () => {
      try {
        const canvas = document.createElement('canvas');
        const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
        
        if (!gl) {
          return 'WebGL not supported';
        }
        
        const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
        if (debugInfo) {
          return gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
        }
        return 'Renderer info unavailable';
      } catch (e) {
        return 'WebGL info unavailable';
      }
    };

    // Function to get battery info if available
    const getBatteryInfo = async () => {
      if (navigator.getBattery) {
        try {
          const battery = await navigator.getBattery();
          return Math.round(battery.level * 100) + '%';
        } catch (e) {
          return 'Not available';
        }
      }
      return 'Not supported';
    };

    // Function to get more accurate system information
    const getSystemInfo = () => {
      // Detect OS name and version
      const userAgent = navigator.userAgent;
      let osName = 'Unknown';
      let osVersion = '';
      let deviceType = 'Desktop';
      let architecture = 'Unknown';
      
      // Note: navigator.platform is deprecated, but we'll still capture it
      // for backward compatibility while using more modern detection methods
      // See: https://developer.mozilla.org/en-US/docs/Web/API/Navigator/platform
      const platform = navigator.platform || 'Unknown';
      
      // Try to use userAgentData if available (modern approach)
      if (navigator.userAgentData) {
        try {
          const platformInfo = navigator.userAgentData.platform;
          if (platformInfo) {
            // userAgentData.platform provides a more reliable platform identifier
            if (/macOS/i.test(platformInfo)) {
              osName = 'macOS';
            } else if (/Windows/i.test(platformInfo)) {
              osName = 'Windows';
            } else if (/Android/i.test(platformInfo)) {
              osName = 'Android';
            } else if (/Linux/i.test(platformInfo)) {
              osName = 'Linux';
            } else if (/iOS/i.test(platformInfo)) {
              osName = 'iOS';
            } else {
              osName = platformInfo;
            }
          }
          
          // Get more detailed information if available
          if (navigator.userAgentData.getHighEntropyValues) {
            navigator.userAgentData.getHighEntropyValues(['platformVersion', 'architecture', 'model'])
              .then(ua => {
                if (ua.platformVersion) {
                  setNetworkInfo(prev => ({
                    ...prev,
                    osVersion: ua.platformVersion
                  }));
                }
                if (ua.architecture) {
                  setNetworkInfo(prev => ({
                    ...prev,
                    architecture: ua.architecture
                  }));
                }
              })
              .catch(err => console.warn('Error getting high entropy values:', err));
          }
        } catch (e) {
          console.warn('Error accessing userAgentData:', e);
        }
      }
      
      // Detect device type
      if (/Mobi|Android|iPhone|iPad|iPod/i.test(userAgent)) {
        deviceType = 'Mobile';
        
        if (/iPad/i.test(userAgent)) {
          deviceType = 'Tablet';
        }
      }
      
      // Fallback to userAgent parsing if we still don't have OS info
      if (osName === 'Unknown') {
        if (/Windows NT/i.test(userAgent)) {
          osName = 'Windows';
          const ntVersion = userAgent.match(/Windows NT (\d+\.\d+)/);
          if (ntVersion) {
            const versionMap = {
              '10.0': '10/11',
              '6.3': '8.1',
              '6.2': '8',
              '6.1': '7',
              '6.0': 'Vista',
              '5.2': 'XP x64',
              '5.1': 'XP',
              '5.0': '2000'
            };
            osVersion = versionMap[ntVersion[1]] || ntVersion[1];
          }
        } else if (/Macintosh/i.test(userAgent)) {
          osName = 'macOS';
          const macVersion = userAgent.match(/Mac OS X (\d+[._]\d+[._]?\d*)/);
          if (macVersion) {
            osVersion = macVersion[1].replace(/_/g, '.');
          }
        } else if (/Linux/i.test(userAgent)) {
          osName = 'Linux';
          if (/Ubuntu/i.test(userAgent)) {
            osName = 'Ubuntu';
            const ubuntuVersion = userAgent.match(/Ubuntu[/\\s]?(\d+\.\d+)/);
            if (ubuntuVersion) {
              osVersion = ubuntuVersion[1];
            }
          }
        } else if (/Android/i.test(userAgent)) {
          osName = 'Android';
          const androidVersion = userAgent.match(/Android (\d+(\.\d+)+)/);
          if (androidVersion) {
            osVersion = androidVersion[1];
          }
        } else if (/iPhone|iPad|iPod/i.test(userAgent)) {
          osName = 'iOS';
          const iosVersion = userAgent.match(/OS (\d+[._]\d+[._]?\d*)/);
          if (iosVersion) {
            osVersion = iosVersion[1].replace(/_/g, '.');
          }
        }
      }
      
      // Detect architecture
      if (/x86_64|x86-64|x64|amd64/i.test(userAgent)) {
        architecture = 'x64';
      } else if (/x86|i386|i686/i.test(userAgent)) {
        architecture = 'x86';
      } else if (/arm64|aarch64/i.test(userAgent)) {
        architecture = 'ARM64';
      } else if (/arm/i.test(userAgent)) {
        architecture = 'ARM';
      }
      
      // Get CPU class if available
      let cpuClass = 'Unknown';
      if (navigator.cpuClass) {
        cpuClass = navigator.cpuClass;
      }
      
      return {
        osName,
        osVersion,
        deviceType,
        architecture,
        cpuClass,
        platform
      };
    };

    // Collect all the network and system information
    const collectInfo = async () => {
      const ipAddresses = await fetchIPAddresses();
      
      // Get connection info if available
      let connectionType = 'Unknown';
      let rtt = 'N/A';
      let downlink = 'N/A';
      
      if (navigator.connection) {
        connectionType = navigator.connection.effectiveType || 'Unknown';
        rtt = navigator.connection.rtt ? `${navigator.connection.rtt} ms` : 'N/A';
        downlink = navigator.connection.downlink ? `${navigator.connection.downlink} Mbps` : 'N/A';
      }

      // Get memory info if available
      let memoryInfo = 'Not available';
      if (navigator.deviceMemory) {
        memoryInfo = `${navigator.deviceMemory} GB`;
      }
      
      // Get system information
      const systemInfo = getSystemInfo();

      // Set all collected information
      setNetworkInfo({
        ip: ipAddresses.primary,
        ipv4: ipAddresses.ipv4,
        ipv6: ipAddresses.ipv6,
        xffIp: ipAddresses.xff,
        userAgent: navigator.userAgent,
        language: navigator.language,
        platform: systemInfo.platform,
        screenRes: `${window.screen.width}×${window.screen.height}`,
        cores: navigator.hardwareConcurrency || 'Unknown',
        connection: connectionType,
        rtt,
        downlink,
        webGLRenderer: getWebGLInfo(),
        batteryLevel: await getBatteryInfo(),
        memory: memoryInfo,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        architecture: systemInfo.architecture,
        deviceType: systemInfo.deviceType,
        osName: systemInfo.osName,
        osVersion: systemInfo.osVersion,
        cpuClass: systemInfo.cpuClass
      });
    };

    collectInfo();

    // Update system time every second
    const timeInterval = setInterval(() => {
      setSystemTime(new Date().toISOString());
    }, 1000);

    return () => clearInterval(timeInterval);
  }, []);

  const toggleSection = (section) => {
    if (expandedSection === section) {
      setExpandedSection(null);
    } else {
      setExpandedSection(section);
    }
  };
  
  // Internet speed test function
  const runSpeedTest = async () => {
    if (speedTest.isRunning) return;
    
    setSpeedTest({
      ...speedTest,
      isRunning: true,
      downloadSpeed: null,
      uploadSpeed: null,
      progress: 0
    });

    // Speed test configuration
    const testConfig = {
      // Test sizes in bytes (1MB, 2MB, 5MB)
      downloadSizes: [1048576, 2097152, 5242880],
      // Number of samples to collect for each size
      samplesPerSize: 2,
      // Maximum test duration in milliseconds
      maxTestDuration: 15000,
      // Minimum valid speed in Mbps
      minValidSpeed: 0.1,
      // Maximum valid speed in Mbps (to filter outliers)
      maxValidSpeed: 1000
    };

    const measureDownloadSpeed = async () => {
      try {
        const allSpeeds = [];
        let totalTests = testConfig.downloadSizes.length * testConfig.samplesPerSize;
        let completedTests = 0;
        
        // Test each file size multiple times
        for (const size of testConfig.downloadSizes) {
          for (let sample = 0; sample < testConfig.samplesPerSize; sample++) {
            try {
              // Update progress
              setSpeedTest(prev => ({ 
                ...prev, 
                progress: Math.floor((completedTests / totalTests) * 90) // Reserve last 10% for processing
              }));
              
              // Create a unique URL with size parameter and cache buster
              const cacheBuster = Date.now() + Math.random().toString(36).substring(2, 15);
              const url = `https://speed.cloudflare.com/__down?bytes=${size}&cachebust=${cacheBuster}`;
              
              // Set up AbortController for timeout
              const controller = new AbortController();
              const timeoutId = setTimeout(() => controller.abort(), testConfig.maxTestDuration);
              
              const startTime = performance.now();
              
              try {
                // Use XMLHttpRequest for more accurate progress tracking
                const speed = await new Promise((resolve, reject) => {
                  const xhr = new XMLHttpRequest();
                  let lastLoadedBytes = 0;
                  let lastProgressTime = startTime;
                  let progressSpeeds = [];
                  
                  xhr.open('GET', url, true);
                  xhr.responseType = 'arraybuffer';
                  
                  xhr.onprogress = (event) => {
                    if (event.lengthComputable) {
                      const currentTime = performance.now();
                      const timeDiff = currentTime - lastProgressTime;
                      
                      // Only calculate speed if enough time has passed (50ms)
                      if (timeDiff > 50) {
                        const loadedBytesDiff = event.loaded - lastLoadedBytes;
                        const bytesPerSecond = (loadedBytesDiff / timeDiff) * 1000;
                        const speedMbps = (bytesPerSecond * 8) / (1024 * 1024);
                        
                        // Store instantaneous speed
                        if (speedMbps > testConfig.minValidSpeed && speedMbps < testConfig.maxValidSpeed) {
                          progressSpeeds.push(speedMbps);
                        }
                        
                        // Update for next calculation
                        lastLoadedBytes = event.loaded;
                        lastProgressTime = currentTime;
                      }
                      
                      // Update UI progress
                      const testProgress = (event.loaded / event.total) * 100;
                      const overallProgress = Math.floor(((completedTests + (testProgress / 100)) / totalTests) * 90);
                      
                      setSpeedTest(prev => ({ 
                        ...prev, 
                        progress: Math.min(overallProgress, 99)
                      }));
                    }
                  };
                  
                  xhr.onload = () => {
                    if (xhr.status === 200) {
                      const endTime = performance.now();
                      const duration = (endTime - startTime) / 1000; // in seconds
                      const bytes = xhr.response.byteLength;
                      const bitsPerSecond = (bytes * 8) / duration;
                      const speedMbps = bitsPerSecond / (1024 * 1024);
                      
                      // If we have progress speeds, use their median for more accuracy
                      if (progressSpeeds.length > 2) {
                        progressSpeeds.sort((a, b) => a - b);
                        const medianProgressSpeed = progressSpeeds[Math.floor(progressSpeeds.length / 2)];
                        resolve(medianProgressSpeed);
                      } else {
                        resolve(speedMbps);
                      }
                    } else {
                      reject(new Error(`HTTP error ${xhr.status}`));
                    }
                  };
                  
                  xhr.onerror = () => reject(new Error('Network error'));
                  xhr.ontimeout = () => reject(new Error('Request timed out'));
                  xhr.onabort = () => reject(new Error('Request aborted'));
                  
                  xhr.send();
                });
                
                // Clear timeout
                clearTimeout(timeoutId);
                
                // Validate and store the speed
                if (speed > testConfig.minValidSpeed && speed < testConfig.maxValidSpeed) {
                  allSpeeds.push(speed);
                  console.log(`Test ${completedTests + 1}/${totalTests}: ${speed.toFixed(2)} Mbps (${(size / 1048576).toFixed(1)}MB)`);
                }
              } catch (error) {
                clearTimeout(timeoutId);
                console.warn(`Test failed for size ${size / 1048576}MB:`, error);
                
                // Try with a fallback method if Cloudflare fails
                try {
                  // Fallback to a simple CDN file download
                  const fallbackUrl = `https://cdn.jsdelivr.net/npm/jquery@3.7.1/dist/jquery.min.js?cachebust=${cacheBuster}`;
                  const fallbackStartTime = performance.now();
                  const response = await fetch(fallbackUrl, { cache: 'no-store' });
                  
                  if (!response.ok) throw new Error('Fallback network response was not ok');
                  
                  const reader = response.body.getReader();
                  let bytesReceived = 0;
                  
                  while (true) {
                    const {done, value} = await reader.read();
                    if (done) break;
                    bytesReceived += value.length;
                  }
                  
                  const fallbackEndTime = performance.now();
                  const fallbackDuration = (fallbackEndTime - fallbackStartTime) / 1000;
                  const fallbackSpeedMbps = ((bytesReceived * 8) / fallbackDuration) / (1024 * 1024);
                  
                  if (fallbackSpeedMbps > testConfig.minValidSpeed && fallbackSpeedMbps < testConfig.maxValidSpeed) {
                    allSpeeds.push(fallbackSpeedMbps);
                    console.log(`Fallback test: ${fallbackSpeedMbps.toFixed(2)} Mbps`);
                  }
                } catch (fallbackError) {
                  console.warn('Fallback test also failed:', fallbackError);
                }
              }
            } finally {
              completedTests++;
            }
          }
        }
        
        // Final progress update
        setSpeedTest(prev => ({ ...prev, progress: 99 }));
        
        if (allSpeeds.length === 0) {
          throw new Error('All speed tests failed');
        }
        
        // Process results - remove outliers and calculate final speed
        const processResults = (speeds) => {
          if (speeds.length <= 2) return speeds;
          
          // Sort speeds
          speeds.sort((a, b) => a - b);
          
          // If we have enough samples, remove the highest and lowest
          if (speeds.length >= 4) {
            speeds = speeds.slice(1, -1);
          }
          
          return speeds;
        };
        
        const processedSpeeds = processResults(allSpeeds);
        
        // Calculate average of the remaining speeds
        const avgSpeed = processedSpeeds.reduce((sum, speed) => sum + speed, 0) / processedSpeeds.length;
        
        // Calculate median for comparison
        const medianSpeed = processedSpeeds[Math.floor(processedSpeeds.length / 2)];
        
        // Use the more conservative of the two
        const finalSpeed = Math.min(avgSpeed, medianSpeed).toFixed(2);
        
        console.log(`Final speed: ${finalSpeed} Mbps (avg: ${avgSpeed.toFixed(2)}, median: ${medianSpeed.toFixed(2)})`);
        
        setSpeedTest(prev => ({ 
          ...prev, 
          downloadSpeed: finalSpeed,
          progress: 100,
          isRunning: false
        }));
      } catch (error) {
        console.error('Speed test failed:', error);
        setSpeedTest(prev => ({ 
          ...prev, 
          downloadSpeed: 'Failed',
          progress: 100,
          isRunning: false
        }));
      }
    };
    
    await measureDownloadSpeed();
  };

  // Add this function before the Card component
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      console.error('Failed to copy:', err);
      return false;
    }
  };

  // IP Address Card component with copy functionality
  const IPAddressCard = () => {
    const [copied, setCopied] = useState(false);
    const [copyType, setCopyType] = useState('');

    const handleCopy = async (e, textToCopy, type) => {
      e.stopPropagation();
      const success = await copyToClipboard(textToCopy);
      if (success) {
        setCopied(true);
        setCopyType(type);
        setTimeout(() => setCopied(false), 2000);
      }
    };

    return (
      <Card 
        title="IP ADDRESS" 
        value={
          <div>
            {networkInfo.ipv4 !== 'Not available' && (
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-gray-500">IPv4:</span> {networkInfo.ipv4}
                </div>
                {expandedSection === 'ip' && (
                  <button
                    onClick={(e) => handleCopy(e, networkInfo.ipv4, 'ipv4')}
                    className="ml-2 p-1 hover:bg-gray-800 rounded transition-colors"
                    title="Copy IPv4 to clipboard"
                  >
                    {copied && copyType === 'ipv4' ? (
                      <Check size={16} className="text-green-400" />
                    ) : (
                      <Copy size={16} className="text-blue-400" />
                    )}
                  </button>
                )}
              </div>
            )}
            
            {networkInfo.ipv6 !== 'Not available' && (
              <div className="flex items-center justify-between mt-2">
                <div className="break-all pr-2">
                  <span className="text-gray-500">IPv6:</span> {networkInfo.ipv6}
                </div>
                {expandedSection === 'ip' && (
                  <button
                    onClick={(e) => handleCopy(e, networkInfo.ipv6, 'ipv6')}
                    className="ml-2 p-1 hover:bg-gray-800 rounded transition-colors flex-shrink-0"
                    title="Copy IPv6 to clipboard"
                  >
                    {copied && copyType === 'ipv6' ? (
                      <Check size={16} className="text-green-400" />
                    ) : (
                      <Copy size={16} className="text-blue-400" />
                    )}
                  </button>
                )}
              </div>
            )}
            
            {networkInfo.xffIp !== 'Not available' && (
              <div className="flex items-center justify-between mt-2">
                <div className="break-all pr-2">
                  <span className="text-gray-500">XFF:</span> {networkInfo.xffIp}
                </div>
                {expandedSection === 'ip' && (
                  <button
                    onClick={(e) => handleCopy(e, networkInfo.xffIp, 'xff')}
                    className="ml-2 p-1 hover:bg-gray-800 rounded transition-colors flex-shrink-0"
                    title="Copy X-Forwarded-For IP to clipboard"
                  >
                    {copied && copyType === 'xff' ? (
                      <Check size={16} className="text-green-400" />
                    ) : (
                      <Copy size={16} className="text-blue-400" />
                    )}
                  </button>
                )}
              </div>
            )}
            
            {networkInfo.ipv4 === 'Not available' && networkInfo.ipv6 === 'Not available' && networkInfo.xffIp === 'Not available' && (
              <div>No IP address available</div>
            )}
          </div>
        }
        icon={<Globe size={16} />}
        id="ip"
      />
    );
  };

  // Styled components
  const Card = ({ title, value, icon, id }) => {
    // Helper function to determine card content height class
    const getExpandedHeightClass = (cardId) => {
      switch (cardId) {
        case 'network': // Network card with speed test has the most content
          return 'max-h-[300px]';
        case 'browser': // Browser info can be quite long
          return 'max-h-[150px]';
        case 'system': // System info is medium length
        case 'display':
          return 'max-h-[100px]';
        case 'ip': // IP card needs more space for IPv6
          return 'max-h-[120px]';
        default: // Default for simple one-line cards
          return 'max-h-[60px]';
      }
    };

    return (
      <div 
        className="bg-gray-900 border border-gray-700 rounded-md p-4 flex flex-col hover:border-blue-500 cursor-pointer transition-all duration-300"
        onClick={() => toggleSection(id)}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <span className="text-blue-400">{icon}</span>
            <h3 className="text-gray-300 font-mono text-sm">{title}</h3>
          </div>
          <span className="text-xs text-blue-500 font-mono">
            {expandedSection === id ? '[-]' : '[+]'}
          </span>
        </div>
        <div className={`text-gray-100 font-mono text-lg overflow-hidden transition-all duration-300 ${expandedSection === id ? getExpandedHeightClass(id) : 'max-h-6'} ${!expandedSection === id && 'truncate'}`}>
          {value}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-gray-950 min-h-screen text-gray-300 p-4 font-mono">
      <header className="border-b border-gray-800 pb-4 mb-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-blue-400 flex items-center">
            <Server className="mr-2" size={24} />
            INFRANET
          </h1>
          <div className="text-xs text-gray-500">
            <span className="mr-2">[SYSTEM]</span>
            <span className="font-bold text-blue-300">{systemTime}</span>
          </div>
        </div>
        <p className="text-gray-500 text-sm mt-1">
          Technical network information for your current browser session
        </p>
        <p className="text-gray-500 text-xs mt-1">
          contact @slalanne if something doesn't work
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <IPAddressCard />
        <Card 
          title="NETWORK" 
          value={
            <div>
              <div className="mb-2">
                <span className="text-gray-500">Connection:</span> {networkInfo.connection} <br />
                <span className="text-gray-500">RTT:</span> {networkInfo.rtt} <br />
                <span className="text-gray-500">Downlink:</span> {networkInfo.downlink}
              </div>
              
              <div className="mt-4 border-t border-gray-800 pt-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-blue-400 text-sm">SPEED TEST</span>
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      runSpeedTest();
                    }} 
                    className={`flex items-center space-x-1 bg-blue-900 hover:bg-blue-800 text-blue-300 px-3 py-1 rounded text-xs ${speedTest.isRunning ? 'opacity-50 cursor-not-allowed' : ''}`}
                    disabled={speedTest.isRunning}
                  >
                    {speedTest.isRunning ? (
                      <>
                        <RotateCw size={12} className="animate-spin" />
                        <span>Testing...</span>
                      </>
                    ) : (
                      <>
                        <Download size={12} />
                        <span>Run Test</span>
                      </>
                    )}
                  </button>
                </div>
                
                {speedTest.isRunning && (
                  <div className="w-full bg-gray-800 h-1 my-2 rounded-full overflow-hidden">
                    <div 
                      className="bg-blue-500 h-1 rounded-full transition-all duration-300" 
                      style={{ width: `${speedTest.progress}%` }}
                    ></div>
                  </div>
                )}
                
                {speedTest.downloadSpeed && (
                  <div className="mt-2 text-lg">
                    <span className="text-gray-500">Download:</span> 
                    <span className="text-blue-400 font-bold ml-2">{speedTest.downloadSpeed} Mbps</span>
                  </div>
                )}
              </div>
            </div>
          }
          icon={<Wifi size={16} />}
          id="network"
        />
        <Card 
          title="SYSTEM" 
          value={
            <div>
              <div>
                <span className="text-gray-500">OS:</span> {networkInfo.osName} {networkInfo.osVersion} <br />
                <span className="text-gray-500">Device:</span> {networkInfo.deviceType} <br />
                <span className="text-gray-500">Architecture:</span> {networkInfo.architecture} <br />
                <span className="text-gray-500">CPU:</span> {networkInfo.cores} cores {networkInfo.cpuClass !== 'Unknown' ? `(${networkInfo.cpuClass})` : ''} <br />
                <span className="text-gray-500">Memory:</span> {networkInfo.memory}
                {/* Platform is shown for reference but marked as deprecated */}
                <span className="text-xs text-gray-600 block mt-1">Platform: {networkInfo.platform} (deprecated)</span>
              </div>
            </div>
          } 
          icon={<Cpu size={16} />}
          id="system"
        />
        <Card 
          title="BROWSER" 
          value={networkInfo.userAgent} 
          icon={<Info size={16} />}
          id="browser"
        />
        <Card 
          title="DISPLAY" 
          value={`${networkInfo.screenRes} | GPU: ${networkInfo.webGLRenderer}`} 
          icon={<Activity size={16} />}
          id="display"
        />
        <Card 
          title="LOCALE" 
          value={`${networkInfo.language} | ${networkInfo.timeZone}`} 
          icon={<Globe size={16} />}
          id="locale"
        />
        <Card 
          title="MEMORY" 
          value={networkInfo.memory} 
          icon={<Database size={16} />}
          id="memory"
        />
        <Card 
          title="BATTERY" 
          value={networkInfo.batteryLevel} 
          icon={<Shield size={16} />}
          id="battery"
        />
        <Card 
          title="TIME" 
          value={systemTime} 
          icon={<Clock size={16} />}
          id="time"
        />
      </div>

      <footer className="mt-8 pt-4 border-t border-gray-800 text-gray-500 text-xs">
        <div className="flex justify-between">
          <div>
            <span className="text-blue-400">[STATUS]:</span> SECURE CONNECTION ESTABLISHED
          </div>
          <div>v1.0.0 | INFRASEC</div>
        </div>
      </footer>
    </div>
  );
};

export default NetworkInfoDashboard;
